"use client";
import { useState, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/tanstack/apiClient";
import { toast } from "@/components/toast";
import { useTranslation } from "@/hooks/useLanguage";
import { userConfig } from "@/queries/pages/dashboard/userApiConfig";
import { roleConfig } from "@/queries/pages/dashboard/roleApiConfig";
import { organizationConfig } from "@/queries/pages/dashboard/organizationApiConfig";
import { entityConfig } from "@/queries/pages/dashboard/masterDataApiConfig";
import { accessMatrixConfig } from "@/queries/pages/dashboard/accessMatrixApiConfig";
import { bookingsConfig } from "@/queries/pages/dashboard/bookingsApiConfig";
import { staffConfig } from "@/queries/pages/dashboard/staffApiConfig";
import { housekeepingConfig } from "@/queries/pages/dashboard/housekeepingApiConfig";
import { registrationConfig } from "@/queries/pages/dashboard/registrationApiConfig";
import { propertyInfoConfig } from "@/queries/pages/dashboard/propertyInfoApiConfig";
import { otpApiConfig } from "@/queries/pages/register/otpApiConfig";
import { guestConfig } from "@/queries/pages/dashboard/guestsApiConfig";
import { propertyComplianceConfig } from "@/queries/pages/dashboard/propertyComplianceApiConfig";

import { logConfig } from "@/queries/pages/dashboard/logApiConfig";
import { profileConfig } from "@/queries/pages/dashboard/profileApiConfig";
import { dynamicPricingConfig } from "@/queries/pages/pricing/pricing-rule.apiconfig";
import { roomTypeRateBoundariesApiConfig } from "@/queries/pages/pricing/pricing-caps.apiconfig";
import { propertyConfig } from "@/queries/pages/dashboard/propertyApiConfig";
import { roomConfig } from "@/queries/pages/dashboard/roomApiConfig";
import { roomTypeConfig } from "@/queries/pages/dashboard/roomTypeApiConfig";
import { buildingConfig } from "@/queries/pages/dashboard/buildingApiConfig";
import { boardPlanConfig } from "@/queries/pages/dashboard/boardPlanApiConfig";
import { ratePlanApiConfig } from "@/queries/pages/pricing/rate-plan.apiconfig";
import { cancellationPolicyApiConfig } from "@/queries/pages/pricing/cancellation-policy.apiconfig";
import { subscriptionConfig } from "@/queries/pages/dashboard/subscriptionApiConfig";
import { subscriptionPlanApiConfig } from "@/queries/pages/pricing/subscription-plan.apiconfig";
import { onboardingConfig } from "@/queries/pages/dashboard/onboardingApiConfig";
import { authConfig } from "@/queries/pages/dashboard/authApiConfig";
import { guestReviewConfig } from "@/queries/pages/dashboard/guestReviewApiConfig";
import { summaryConfig } from "@/queries/pages/dashboard/ReviewSummaryApiConfig";
import { earlyDeparturePolicyApiConfig } from "@/queries/pages/pricing/early-departure-policy.apiconfig";
import { notificationTemplatesConfig } from "@/queries/pages/dashboard/notificationApiConfig";
import { maintenanceConfig } from "@/queries/pages/dashboard/maintenanceApiConfig";
import { grievanceConfig } from "@/queries/pages/dashboard/grievanceApiConfig";
import { calendarConfig } from "@/queries/pages/dashboard/calendarApiConfig";
import { zatcaConfig } from "@/queries/pages/dashboard/zatcaApiConfig";
import { propertyAddonConfig } from "@/queries/pages/dashboard/propertyAddonApiConfig";
import { folioConfig } from "@/queries/pages/dashboard/folioApiConfig";
import { bookingAddonConfig } from "@/queries/pages/dashboard/bookingAddonApiConfig";
import { propertyPolicyConfig } from "@/queries/pages/dashboard/propertyPolicyApiConfig";
import { paymentTypesConfig } from "@/queries/pages/dashboard/paymentTypesApiConfig";
import { faqConfig } from "@/queries/pages/dashboard/faqApiConfig";
import { chatbotConfig } from "@/queries/pages/dashboard/chatbotApiConfig";
import { consumableItemConfig } from "@/queries/pages/dashboard/consumableItemApiConfig";
import { parLevelConfig } from "@/queries/pages/dashboard/parLevelApiConfig";
import { roomInventoryConfig } from "@/queries/pages/dashboard/roomInventoryApiConfig";
import { inspectionConfig } from "@/queries/pages/dashboard/inspectionApiConfig";
import { holdsConfig } from "@/queries/pages/dashboard/holdsApiConfig";

const ALL_API_CONFIGS = {
  ...userConfig,
  ...roleConfig,
  ...organizationConfig,
  ...entityConfig,
  ...accessMatrixConfig,
  ...bookingsConfig,
  ...staffConfig,
  ...housekeepingConfig,
  ...registrationConfig,
  ...propertyInfoConfig,
  ...otpApiConfig,
  ...guestConfig,
  ...propertyComplianceConfig,

  ...logConfig,
  ...profileConfig,
  ...dynamicPricingConfig,
  ...roomTypeRateBoundariesApiConfig,
  ...propertyConfig,
  ...roomConfig,
  ...roomTypeConfig,
  ...buildingConfig,
  ...boardPlanConfig,
  ...ratePlanApiConfig,
  ...cancellationPolicyApiConfig,
  ...subscriptionConfig,
  ...subscriptionPlanApiConfig,
  ...onboardingConfig,
  ...authConfig,
  ...guestReviewConfig,
  ...summaryConfig,
  ...earlyDeparturePolicyApiConfig,
  ...notificationTemplatesConfig,
  ...maintenanceConfig,
  ...grievanceConfig,
  ...calendarConfig,
  ...zatcaConfig,
  ...propertyAddonConfig,
  ...folioConfig,
  ...bookingAddonConfig,
  ...propertyPolicyConfig,
  ...paymentTypesConfig,
  ...faqConfig,
  ...chatbotConfig,
  ...consumableItemConfig,
  ...parLevelConfig,
  ...roomInventoryConfig,
  ...inspectionConfig,
  ...holdsConfig,
};

export function useMutationQuery() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [activeMutations, setActiveMutations] = useState({});
  // Generic mutation function that works with any API config
  const executeMutation = useCallback(async ({ key, data, headers }) => {
    const cfg = ALL_API_CONFIGS[key];
    if (!cfg) console.log(`API config not found: ${key}`);
    // Build URL (handle function URLs)
    const url = typeof cfg.url === "function" ? cfg.url(data) : cfg.url;
    // Build the correct options for apiClient
    const options = {
      method: cfg.method,
      // Pass timeout from config if specified (for long-running operations like inventory initialization)
      ...(cfg.options?.timeout && { timeout: cfg.options.timeout }),
      // Custom per-call headers (e.g. Idempotency-Key on subscription upgrade approval).
      // Merged on top of apiClient's default headers (auth, csrf, accept-language, content-type).
      ...(headers && Object.keys(headers).length > 0 && { headers }),
    };
    // Add body for POST/PUT/PATCH/DELETE methods
    if (["POST", "PUT", "PATCH", "DELETE"].includes(cfg.method) && data) {
      // For function URLs, exclude 'id' from body since it's in the URL
      let bodyData = data;
      if (
        typeof cfg.url === "function" &&
        data &&
        typeof data === "object" &&
        !(data instanceof FormData)
      ) {
        const {
          id,
          lineNumber,
          grievanceId,
          docId,
          token,
          propertyId,
          organizationId,
          roleId,
          // holdId NOT stripped — amendLine needs it in the body.
          // UpdateHoldDto accepts holdId as optional so updateHold still works.
          taskId,
          bookingId,
          roomId,
          inspectionId,
          buildingId,
          floorId,
          maintenanceId,
          requestId,
          ...rest
        } = data;
        bodyData = rest;
      }
      // Don't stringify FormData - pass it directly
      options.body =
        bodyData instanceof FormData ? bodyData : JSON.stringify(bodyData);
    }
    const response = await apiClient(url, options);
    // Check if response is ok
    if (!response.ok) {
      return Promise.reject(
        new Error(
          JSON.stringify({
            message: response.message || "API request failed",
            data: response.data,
            key: response.data?.key,
          }),
        ),
      );
    }

    return { data: response.data, config: cfg };
  }, []);
  // Create a mutation hook that TanStack Query tracks
  const mutation = useMutation({
    mutationFn: executeMutation,
    onSuccess: ({ data, config }, variables) => {
      const { key } = variables;
      // Invalidate cache keys
      config.options?.invalidateKeys?.forEach((k) =>
        queryClient.invalidateQueries({ queryKey: [k] }),
      );

      // Skip automatic toast for operations that handle their own toast messages
      const skipToast = ["seedRoomDefaults", "createHold", "refreshHold", "releaseHold", "getUserHolds", "previewAmendLine", "previewExtendStay"];
      if (!skipToast.includes(key)) {
        toast({
          type: "success",
          title: t("common.success") || "Success",
          message:
            (data?.key && t(data.key) !== data.key
              ? t(data.key)
              : data?.message) || `${key} completed successfully.`,
        });
      }

      // Update success state
      setActiveMutations((prev) => ({
        ...prev,
        [key]: { ...prev[key], isSuccess: true },
      }));
    },
    onError: (error, variables) => {
      const { key } = variables;

      let backendErrorMsg = null;
      try {
        // Parse the stringified error from executeMutation
        const errorData = JSON.parse(error.message);

        // Try to translate the backend messageKey first
        const messageKey = errorData?.data?.messageKey || errorData?.messageKey || errorData?.key;
        if (messageKey) {
          const translated = t(messageKey);
          if (translated !== messageKey) {
            backendErrorMsg = translated;
          }
        }

        // Try data.errors as a translation key (e.g. "booking.guest_already_checked_in")
        if (!backendErrorMsg) {
          const errorsKey = errorData?.data?.errors;
          if (errorsKey && typeof errorsKey === "string") {
            const translated = t(errorsKey);
            backendErrorMsg = translated !== errorsKey ? translated : errorsKey;
          }
        }

        // Fallback to the plain message string
        if (!backendErrorMsg) {
          backendErrorMsg = errorData?.data?.message || errorData?.message;
        }

        // Ensure we got a plain string, not an object
        if (backendErrorMsg && typeof backendErrorMsg === "object") {
          backendErrorMsg = backendErrorMsg.message || null;
        }
      } catch {
        // Fallback for non-JSON errors
        if (error.message && !error.message.startsWith("{")) {
          const translated = t(error.message);
          backendErrorMsg = translated !== error.message ? translated : error.message;
        }
      }

      // Ensure we never show raw JSON or objects
      if (
        !backendErrorMsg ||
        (typeof backendErrorMsg === "string" && backendErrorMsg.startsWith("{"))
      ) {
        backendErrorMsg = t("common.operation_failed") || "Operation failed. Please try again.";
      }

      // Final safety: strip any remaining JSON from message
      if (typeof backendErrorMsg === "string" && backendErrorMsg.includes('"messageKey"')) {
        try {
          const parsed = JSON.parse(backendErrorMsg);
          backendErrorMsg = parsed?.message || parsed?.data?.message || "Operation failed. Please try again.";
        } catch {
          backendErrorMsg = "Operation failed. Please try again.";
        }
      }

      // Skip error toast for hold operations (handled by useBookingHold)
      const skipErrorToast = ["createHold", "refreshHold", "releaseHold", "getUserHolds", "previewAmendLine", "previewExtendStay"];
      if (!skipErrorToast.includes(key)) {
        toast({
          type: "error",
          title: t("common.error") || "Error",
          message: backendErrorMsg,
        });
      }
      // Update error state
      setActiveMutations((prev) => ({
        ...prev,
        [key]: { ...prev[key], error },
      }));
    },
    onMutate: (variables) => {
      const { key } = variables;
      // Track which mutation is starting
      setActiveMutations((prev) => ({
        ...prev,
        [key]: { isLoading: true, isSuccess: false, error: null },
      }));
    },
    onSettled: (_data, _error, variables) => {
      const { key } = variables;
      // Clear loading state
      setActiveMutations((prev) => ({
        ...prev,
        [key]: { ...prev[key], isLoading: false },
      }));
    },
  });
  // Wrapper function to maintain the same API.
  // Third arg is optional: { headers } for per-call custom headers (e.g. Idempotency-Key).
  // Existing callers pass only (key, data) — fully backwards-compatible.
  const handleApiAction = useCallback(
    async (key, data, opts) => {
      try {
        return await mutation.mutateAsync({ key, data, headers: opts?.headers });
      } catch (err) {
        // onError already shows the toast — return error info instead of throwing
        // so callers don't get unhandled rejections, but can still inspect the error
        return { __mutationError: true, error: err };
      }
    },
    [mutation],
  );
  // Helper functions to check state of specific mutations
  const isLoading = useCallback(
    (key) => {
      return activeMutations[key]?.isLoading || false;
    },
    [activeMutations],
  );
  const isSuccess = useCallback(
    (key) => {
      return activeMutations[key]?.isSuccess || false;
    },
    [activeMutations],
  );
  const error = useCallback(
    (key) => {
      return activeMutations[key]?.error || null;
    },
    [activeMutations],
  );
  return {
    handleApiAction,
    isLoading,
    isSuccess,
    error,
    // Expose mutation object for advanced use cases
    mutation,
  };
}
